import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  IconChevronDown,
  IconCheck,
  IconMenu2,
  IconInbox,
  IconTicket,
} from '@tabler/icons-react';
import { GlobalSearchBar } from '@/components/global-search/GlobalSearchBar';
import { UserMenu } from '@/components/ui/user-menu/UserMenu';
import { TopBarIconButton } from '@/components/ui/top-bar-icon-button';
import { useAuth } from '@/hooks/useAuth';

export interface ModuleConfig {
  key: string;
  icon: React.ReactNode;
  name: string;
  href: string;
  /** Unread/pending count rendered right-aligned in the module switcher row. */
  badge?: number;
}

export interface ModuleTopBarProps {
  currentModule: ModuleConfig | null;
  allModules: ModuleConfig[];
  onBackToLauncher: () => void;
  loading?: boolean;
  /** Opens the mobile navigation drawer. Hamburger renders only when provided. */
  onOpenMobileNav?: () => void;
  /** Render the top-bar Inbox icon button (user has Conversations access). */
  showInbox?: boolean;
  inboxCount?: number;
  /** Already-translated Inbox label. */
  inboxLabel?: string;
  /** Render the top-bar HQ Queue icon button (user's nav includes support-tickets). */
  showHqQueue?: boolean;
  hqQueueCount?: number;
  /** Already-translated HQ Queue label. */
  hqQueueLabel?: string;
  /** Optional assistant toggle rendered before the Inbox button. */
  assistantSlot?: React.ReactNode;
}

export function ModuleTopBar({
  currentModule,
  allModules,
  onBackToLauncher,
  loading = false,
  onOpenMobileNav,
  showInbox = false,
  inboxCount = 0,
  inboxLabel = 'Inbox',
  showHqQueue = false,
  hqQueueCount = 0,
  hqQueueLabel = 'HQ Queue',
  assistantSlot,
}: ModuleTopBarProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!dropdownOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDropdownOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [dropdownOpen]);

  const handleModuleSelect = (module: ModuleConfig) => {
    setDropdownOpen(false);
    if (currentModule?.key !== module.key) {
      navigate(module.href);
    }
  };

  return (
    <header
      className="tee-dots-surface flex items-center h-14 px-3 md:px-6 bg-[var(--color-forest)] border-b border-[var(--color-forest-deep)] flex-shrink-0 z-40 relative"
      style={{ minHeight: '56px' }}
    >
      {/* Hamburger — mobile only. The module dropdown, global search and the
          sub-nav bar all move into the drawer it opens; on a phone they don't
          fit the bar and were overflowing the viewport. */}
      {onOpenMobileNav && (
        <button
          onClick={onOpenMobileNav}
          className="md:hidden flex items-center justify-center h-9 w-9 -ml-1 mr-1 rounded-lg hover:bg-white/10 transition-colors focus:outline-none flex-shrink-0"
          aria-label="Open navigation menu"
        >
          <IconMenu2 size={22} className="text-[var(--text-on-forest)]" />
        </button>
      )}


      {/* Logo — back to launcher */}
      <button
        onClick={onBackToLauncher}
        className="flex items-center h-8 w-14 md:w-[72px] overflow-hidden mr-3 md:mr-6 flex-shrink-0 focus:outline-none"
        aria-label="Back to launcher"
      >
        <img
          src="/assets/logo-white.svg"
          alt="Tee Box Logo"
          className="h-full w-full object-contain"
        />
      </button>

      {/* Divider — dropped on mobile, where the gutter alone separates them */}
      <div className="hidden md:block h-6 w-px bg-[var(--text-on-forest-muted)] mr-6 flex-shrink-0" />

      {/* Module switcher — desktop only; mobile uses the drawer */}
      <div className="relative flex-shrink-0 hidden md:block">
        {loading ? (
          <div className="h-8 w-36 rounded-lg bg-[var(--background-page-content)] animate-pulse" aria-hidden="true" />
        ) : (
          <>
            <button
              ref={triggerRef}
              onClick={() => setDropdownOpen((prev) => !prev)}
              className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white/10 transition-colors focus:outline-none"
              aria-haspopup="menu"
              aria-expanded={dropdownOpen}
            >
              {currentModule ? (
                <>
                  <span className="text-[var(--color-gold-500)] flex-shrink-0 flex items-center">
                    {currentModule.icon}
                  </span>
                  <span className="text-[14px] font-medium text-[var(--text-on-forest)]">
                    {currentModule.name}
                  </span>
                </>
              ) : (
                <span className="text-[14px] font-medium text-[var(--text-on-forest-muted)]">
                  Select module
                </span>
              )}
              <IconChevronDown
                size={14}
                className={`text-[var(--text-on-forest-muted)] transition-transform flex-shrink-0 ${dropdownOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {/* Dropdown */}
            {dropdownOpen && (
              <div
                ref={dropdownRef}
                role="menu"
                className="absolute top-full left-0 mt-2 w-60 bg-[var(--background-page-content)] border border-[var(--border-default)] rounded-xl overflow-hidden z-50"
                style={{ boxShadow: 'var(--section-shadow)' }}
              >
                {/* 70vh, not a fixed px cap: at 44px/row every module fits without
                    scrolling on any normal viewport today, and the vh-relative cap
                    still protects short/split-screen viewports if more sections are
                    added later (Box Support was invisible below-the-fold at the old
                    fixed 320px cap once an 8th module pushed past it). */}
                <div className="max-h-[70vh] overflow-y-auto">
                {allModules.map((module) => {
                  const isActive = currentModule?.key === module.key;
                  return (
                    <button
                      key={module.key}
                      role="menuitem"
                      aria-current={isActive ? 'page' : undefined}
                      onClick={() => handleModuleSelect(module)}
                      className={`w-full flex items-center gap-3 px-4 text-left transition-colors focus:outline-none ${
                        isActive
                          ? 'border-l-2 border-[var(--color-forest)] bg-[var(--background-card-elevated)] text-[var(--color-forest)] pl-[14px]'
                          : 'border-l-2 border-transparent hover:bg-[var(--background-card-elevated)] text-[var(--text-content-title)] pl-[14px]'
                      }`}
                      style={{ height: '44px', fontSize: '14px' }}
                    >
                      <span
                        className={`flex items-center flex-shrink-0 ${isActive ? 'text-[var(--color-forest)]' : 'text-[var(--text-content-sub-title)]'}`}
                        style={{ fontSize: '16px' }}
                      >
                        {module.icon}
                      </span>
                      <span className="font-medium">{module.name}</span>
                      {module.badge !== undefined && module.badge > 0 && (
                        <span className="ml-auto text-[11px] bg-[var(--color-gold-subtle)] text-[var(--color-text-gold)] border border-[var(--color-gold-border)] px-1.5 rounded-full">
                          {module.badge}
                        </span>
                      )}
                      {isActive && (
                        <span className={module.badge ? '' : 'ml-auto'}>
                          <IconCheck size={14} className="text-[var(--color-forest)]" />
                        </span>
                      )}
                    </button>
                  );
                })}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {assistantSlot && (
        <div className="mr-2 md:mr-3 flex items-center">{assistantSlot}</div>
      )}

      {showInbox && (
        <TopBarIconButton
          icon={<IconInbox size={22} />}
          label={inboxLabel}
          count={inboxCount}
          active={pathname.startsWith('/inbox')}
          onClick={() => navigate('/inbox')}
          className="mr-2 md:mr-5"
        />
      )}

      {showHqQueue && (
        <TopBarIconButton
          icon={<IconTicket size={22} />}
          label={hqQueueLabel}
          count={hqQueueCount}
          active={pathname.startsWith('/support/tickets')}
          onClick={() => navigate('/support/tickets?status=open')}
          className="mr-2 md:mr-5"
        />
      )}

      {/* Global search — desktop only (fixed 256px; lives in the drawer on mobile) */}
      {user && (
        <div className="mr-4 hidden md:block">
          <GlobalSearchBar />
        </div>
      )}

      {/* User avatar + menu (shared with the launcher header) */}
      {user && <UserMenu />}
    </header>
  );
}
